/** Operator dashboard (/ops). Merged into en.ts under "ops". */
export const opsEn = {
  badge: "Ops",
  nav: {
    overview: "Overview",
    organizations: "Organizations",
    partners: "IT partners",
    users: "People",
    delivery: "Delivery",
    graph: "Microsoft Graph",
    emails: "Emails",
    operators: "Operators",
    audit: "Audit log",
    account: "My account",
    system: "System",
    settings: "Settings",
    bugReports: "Problem reports",
    leads: "Leads",
    events: "Events",
  },
  partners: {
    title: "IT partners",
    description:
      "Partner companies that manage customers through the partner portal: their customers, what they pay and who signs in. Open a partner for details, invoice applications and suspending.",
    empty: "No IT partner has registered yet.",
    name: "Partner",
    code: "Code",
    customers: "Customers",
    users: "Accounts",
    created: "Registered",
    suspended: "Suspended",
    suspend: "Suspend",
    resume: "Resume",
    suspendTitle: "Suspend {{name}}?",
    suspendText:
      "All partner accounts are signed out and lose access to every customer. The customers keep their data. Customers billed to the partner pay themselves after the paid period; resuming does not switch that back.",
    suspendedToast: "Partner suspended.",
    resumed: "Partner resumed.",
    statPartners: "Partners",
    statSuspended_one: "{{count}} suspended",
    statSuspended_other: "{{count}} suspended",
    statCustomers: "Linked customers",
    statBilled_one: "{{count}} billed to a partner",
    statBilled_other: "{{count}} billed to partners",
    statMonthly: "Monthly through partners",
    statMonthlyHint: "at today's number of people, excl. VAT",
    statOpen: "Open invoice applications",
    statAttention_one: "{{count}} customer needs a look",
    statAttention_other: "{{count}} customers need a look",
    search: "Search name or code",
    monthly: "Per month",
    attention: "Need a look",
    lastLogin: "Last sign-in",
    never: "Never",
    noPayment: "No payment method",
    payment: "Payment",
    subscriptionLabel: "Stripe subscription",
    subscription: {
      active: "Active",
      trialing: "Trial",
      past_due: "Payment failed",
      unpaid: "Unpaid",
      canceled: "Canceled",
      incomplete: "Incomplete",
      incomplete_expired: "Expired",
      paused: "Paused",
    },
    until: "until {{date}}",
    noSubscription: "None yet (created when the first customer is due)",
    usersText_one: "{{count}} account",
    usersText_other: "{{count}} accounts",
    person: "Person",
    secondFactor: "Second factor",
    totp: "Authenticator app",
    passkeys_one: "{{count}} passkey",
    passkeys_other: "{{count}} passkeys",
    noSecondFactor: "None",
    invited: "Invited",
    invitedUntil: "invitation valid until {{date}}",
    noCustomers: "No customers yet.",
    billingLabel: "Billing",
    paysPartner: "Partner pays",
    paysCustomer: "Customer pays",
    partnerUntil: "partner pays until {{date}}",
    link: { pending: "Awaiting approval", unlinked: "Link ended" },
    reports: "Monthly reports",
    reportCustomers_one: "{{count}} customer",
    reportCustomers_other: "{{count}} customers",
    billed: "Billed to partner",
    invoiceRequested: "Invoice requested",
    invoiceApproved: "Pays by invoice",
    card: "Card",
    pastDue: "Payment failed",
    review: "Review",
    reviewTitle: "Payment by invoice for {{name}}",
    reviewText:
      "The partner pays monthly by invoice, due within 10 days, instead of by card. Approve only partners you trust with credit.",
    invoiceEmail: "Invoice email",
    invoiceAddress: "Billing address",
    approve: "Approve",
    reject: "Decline",
    approved: "Payment by invoice approved.",
    rejected: "Application declined.",
  },
  elevate: {
    title: "Confirm it's you",
    description:
      "Enter the current code from your authenticator app. Sensitive actions then work for 10 minutes.",
    confirm: "Confirm",
    zuvoDescription:
      "Sign in to zuvo again. Sensitive actions then work for 10 minutes; repeat the action afterwards.",
    zuvoButton: "Confirm with zuvo",
  },
  login: {
    title: "Kontaktfix operations",
    codeSubtitle: "Enter the code from your authenticator app.",
    password: "Password",
    code: "Authentication code",
    recoveryCode: "Recovery code",
    recoveryHint: "Each recovery code works once.",
    signIn: "Sign in",
    useRecovery: "Use a recovery code",
    useApp: "Use the authenticator app",
    breakGlass: "Emergency sign-in",
    breakGlassHint: "Only while the zuvo login is unavailable. Every sign-in is logged.",
    notConfigured: "Sign-in for operations is not set up.",
    errors: {
      zuvo_no_access: "You have no access to Kontaktfix. Access is granted in the Zuvo Hub.",
      zuvo_mfa: "Signing in with zuvo needs a second factor (passkey).",
      zuvo_stale: "Your zuvo sign-in is too old. Please sign in again.",
      zuvo_invalid: "The zuvo sign-in was incomplete. Please try again.",
      zuvo_disabled: "Your operator account is disabled in Kontaktfix.",
      zuvo_failed: "Signing in with zuvo did not work. Please try again.",
      zuvo_unavailable: "The zuvo login cannot be reached right now.",
    },
  },
  setup: {
    title: "Welcome, {{name}}",
    resetTitle: "Set up your access again, {{name}}",
    validUntil: "This link works until {{date}}.",
    stepOf: "Step {{current}} of {{total}}",
    steps: { password: "Password", authenticator: "Authenticator", codes: "Recovery codes" },
    scan: "Scan the QR code with your authenticator app (1Password, Microsoft Authenticator, Google Authenticator and others).",
    qrAlt: "QR code for the authenticator app",
    manual: "Can't scan? Enter the key by hand",
    enterCode: "Code from the app",
    verify: "Verify and finish",
    finish: "Go to the dashboard",
    invalidTitle: "This link no longer works",
    invalidText: "Setup links work once and expire after 72 hours. Ask another operator for a new link.",
  },
  password: {
    new: "New password",
    confirm: "Repeat password",
    hint: "At least 12 characters. A passphrase from a password manager is best.",
    tooShort: "Use at least 12 characters.",
    mismatch: "The passwords do not match.",
  },
  recovery: {
    explain:
      "If you lose your phone, each of these codes signs you in once instead of the authenticator app. Store them in your password manager. They are shown only now.",
    copy: "Copy codes",
    download: "Download",
    saved: "I saved the codes",
    fileHeader: "Kontaktfix operations: recovery codes for {{email}}. Each code works once.",
  },
  overview: {
    title: "Overview",
    description: "The whole platform at a glance.",
    maintenanceOn: "Maintenance mode is on: customers see the maintenance page.",
    mrr: "Monthly recurring revenue",
    mrrHint: "Active and past-due subscriptions, today's number of people",
    subscriptions: "Subscriptions",
    subscriptionsHint: "{{trialing}} in trial · {{pastDue}} payment overdue",
    leads: "Leads",
    last30Days: "Last 30 days",
    events: "Events",
    eventsHint: "Last 30 days · {{count}} waiting for the Zuvo Hub",
    acquisition: "Where organizations come from",
    acquisitionHint: "UTM parameters of the sign-up link, last 90 days. No cookies involved.",
    source: "Source / medium",
    campaign: "Campaign",
    subscribed: "Subscribed",
    direct: "Direct or unknown",
    noSignups: "No new organizations in the last 90 days.",
    orgs: "Organizations",
    orgsHint: "{{connected}} delivering · {{suspended}} suspended",
    users: "People who sign in",
    usersHint: "{{count}} active in the last 30 days",
    contacts: "Contacts",
    recipients: "Mailboxes receiving",
    recipientsHint_one: "{{count}} needs attention",
    recipientsHint_other: "{{count}} need attention",
    runs: "Sync runs, 24 hours",
    runsHint: "{{failed}} failed · {{errors}} with errors",
    emails: "Emails, 7 days",
    emailsHint: "{{count}} not delivered",
    attentionTitle: "Needs attention",
    allGood: "Nothing needs attention.",
    attention: {
      connection_error: "Connection to Microsoft 365 lost",
      certificate_expiring: "Certificate expires on {{date}}",
      sync_failed: "Sync failed",
      recipient_problems_one: "{{count}} mailbox cannot be reached",
      recipient_problems_other: "{{count}} mailboxes cannot be reached",
    },
    signups: "New organizations",
    signupsDescription: "{{count}} in the last 30 days",
    signupsTotal: "in 12 weeks",
    weekOf: "Week of {{date}}",
    newOrgs_one: "{{count}} new organization",
    newOrgs_other: "{{count}} new organizations",
    thisWeek: "This week",
  },
  orgs: {
    title: "Organizations",
    description: "Every customer. Open one for details, support access and actions.",
    search: "Search by name or tenant ID",
    empty: "No organizations found.",
    suspended: "Suspended",
    columns: {
      name: "Organization",
      connection: "Microsoft 365",
      contacts: "Contacts",
      receiving: "Receiving",
      problems: "Problems",
      members: "Members",
      lastSync: "Last delivery",
      created: "Created",
    },
  },
  org: {
    limits: {
      title: "Contact limits",
      perRecipient: "Contacts per person",
      perOrganization: "Contacts in the organization",
      default: "{{value}} (default)",
      hint: "Leave empty for the default. Raise only after talking to the customer: phones get slow with very many contacts, and the first delivery takes longer.",
      saved: "Limits saved.",
    },
    billing: {
      title: "Billing",
      users: "Users and monthly total",
      trial: "Free trial",
      trialRange: "{{from}} to {{to}}",
      trialNotStarted: "Starts with the first delivery",
      subscription: "Stripe subscription",
      cancelAtPeriodEnd: "cancels at period end",
      customer: "Stripe customer",
      extend_one: "Extend trial by {{count}} day",
      extend_other: "Extend trial by {{count}} days",
      extended: "Trial extended. If delivery was paused, a full sync is running.",
    },
    support: "Open as support",
    sync: "Sync changes",
    fullSync: "Full sync (re-check every mailbox)",
    testConnection: "Test connection",
    connectionOk: "The connection works.",
    connectionFailed: "Connection failed: {{error}}",
    suspend: "Suspend",
    resume: "Resume",
    resumed: "Organization resumed. A full sync is running.",
    suspended: "Organization suspended.",
    suspendedSince: "Suspended since {{date}}",
    suspendTitle: "Suspend {{name}}?",
    suspendText:
      "Members can no longer work in Kontaktfix and nothing is delivered. Contacts already in the mailboxes stay. You can resume at any time.",
    reason: "Reason",
    reasonHint: "Internal. Shown to operators only.",
    delete: "Delete organization",
    deleted: "Organization deleted.",
    deleteTitle: "Delete {{name}}?",
    deleteText:
      "This permanently deletes {{count}} contacts, all lists, members and history. Contacts already delivered stay in the mailboxes: to remove them, unassign the lists first and let a sync run.",
    typeName: "Type {{name}} to confirm",
    editTitle: "Edit organization",
    languageHint: "Language of emails and of new defaults.",
    details: "Details",
    tenantId: "Tenant ID",
    language: "Language",
    delivery: "Delivery",
    deliveryModes: { folder: "Own folder", main: "Main contacts folder" },
    connectionMethod: "Connected with",
    methods: { consent: "Kontaktfix app (consent)", own_app: "Own app registration" },
    connection: "Connection",
    checked: "checked {{when}}",
    clientId: "Application ID",
    notVerified: "Not verified yet",
    certificate: "Certificate",
    certificateExpires: "Expires {{date}} ({{days}} days)",
    credential: "Credential",
    secret: "Client secret",
    created: "Created",
    recipients: "Mailboxes",
    lists: "Lists",
    assignments_one: "{{count}} assignment",
    assignments_other: "{{count}} assignments",
    tabs: {
      overview: "Overview",
      members: "Members",
      delivery: "Delivery",
      activity: "Activity",
      emails: "Emails",
    },
  },
  users: {
    title: "People",
    description: "Everyone who signed in to the customer app with Microsoft.",
    search: "Search by name or email",
    empty: "No one found.",
    noOrg: "No organization",
    signOutHint: "Sign out everywhere",
    signedOut_one: "{{count}} session ended.",
    signedOut_other: "{{count}} sessions ended.",
    columns: {
      orgs: "Organizations",
      lastLogin: "Last sign-in",
      firstLogin: "First sign-in",
      sessions: "Sessions",
      added: "Added",
    },
  },
  delivery: {
    title: "Delivery",
    description: "Job queues, recent sync runs and mailboxes with problems across all organizations.",
    active: "active",
    queued: "queued",
    deferred: "{{count}} deferred",
    failedJobs: "{{count}} failed",
    problems: "Mailboxes with problems",
    problemsHint: "Most recent first, up to 200.",
    person: "Person",
    lastAttempt: "Last attempt",
    failed: "Failed",
  },
  graph: {
    title: "Microsoft Graph",
    description:
      "Every request to Microsoft Graph, live. Throttled means Microsoft answered 429, 503 or 504, including single requests inside a batch. Kept 30 days.",
    pause: "Pause",
    resume: "Live",
    live: "Live, every 3 seconds",
    export: "Export CSV",
    window: "Time range",
    windows: { 15: "Last 15 minutes", 60: "Last hour", 360: "Last 6 hours", 1440: "Last 24 hours" },
    allOrgs: "All organizations",
    requests: "Requests",
    throttled: "Throttled",
    errors: "Errors",
    avg: "Average duration",
    maxRetryAfter: "Longest Retry-After",
    none: "none",
    perMinute: "Requests per minute",
    perMinuteHint: "Orange: throttled requests.",
    chartLabel: "Requests per minute, throttled requests highlighted",
    tooltip: "{{requests}} requests, {{throttled}} throttled, {{errors}} errors",
    byOrg: "By organization",
    byResource: "By endpoint",
    lastThrottled: "Last throttled at {{time}}",
    resource: "Endpoint",
    log: "Requests",
    logHint: "Newest first, at most 200. Use the CSV export for older requests.",
    filters: { all: "All", throttled: "Throttled", errors: "Errors" },
    empty: "No requests in this time range.",
    time: "Time",
    duration: "Duration",
    attempt: "Attempt",
    attemptShort: "attempt {{n}}",
    batch: "Batch",
    batchShort: "{{size}} ({{throttled}} throttled)",
    batchDetail: "{{size}} requests, {{throttled}} throttled, {{failed}} failed",
    retryAfter: "Retry-After",
    mailbox: "Mailbox",
    path: "Path",
    error: "Error",
    requestId: "Request ID",
    headers: "Throttling headers",
    network: "Network",
  },
  emails: {
    title: "Emails",
    description: "Notifications sent with React Email and Resend, and their delivery status.",
    setup: "Sending",
    sending: "Resend",
    resendOn: "Sending",
    resendOff: "Not sending: RESEND_API_KEY is missing, emails are only logged",
    from: "From",
    replyTo: "Reply-to",
    webhook: "Delivery status",
    webhookOn: "Resend webhook active",
    webhookOff: "Off (RESEND_WEBHOOK_SECRET not set)",
    templates: "Templates",
    templatesHint: "Preview with sample data.",
    preview: "Preview",
    test: "Send test email",
    send: "Send",
    recipient: "Recipient",
    testResult: { sent: "Test email sent.", logged: "Logged only: sending is not configured." },
    testFailed: "Sending failed: {{error}}",
    log: "Log",
    search: "Search recipient or subject",
    allStatuses: "All statuses",
    empty: "No emails.",
    platform: "Platform",
    columns: { sent: "Sent", to: "To", subject: "Subject" },
    status: {
      sending: "Sending",
      sent: "Sent",
      logged: "Logged only",
      failed: "Failed",
      delivered: "Delivered",
      delayed: "Delayed",
      bounced: "Bounced",
      complained: "Marked as spam",
      suppressed: "Suppressed",
    },
  },
  admins: {
    title: "Operators",
    description:
      "Kontaktfix staff with access to this dashboard. Each has their own password and authenticator app.",
    invite: "Invite operator",
    inviteTitle: "Invite an operator",
    inviteText: "They receive a link to set a password and connect an authenticator app.",
    sendInvite: "Send invitation",
    emailLanguage: "Email language",
    linkSent: "Setup link sent by email. It works once, until {{date}}.",
    linkWarning: "Share this link only with that person, through a channel you trust.",
    linkValid: "Link valid until {{date}}",
    resetDone: "New setup link",
    you: "You",
    locked: "Locked",
    status: { invited: "Invited", active: "Active", disabled: "Disabled" },
    recoveryCodes: "Recovery codes",
    codesLeft_one: "{{count}} left",
    codesLeft_other: "{{count}} left",
    resend: "Send a new link",
    reset: "Reset access",
    unlock: "Unlock",
    enable: "Enable",
    disable: "Disable",
    securityNote:
      "Sensitive actions (operators, support access, suspending or deleting organizations) ask for a fresh authenticator code. Everything operators do is in the audit log.",
    done: {
      disable: "{{name}} is disabled.",
      enable: "{{name}} is enabled.",
      unlock: "{{name}} is unlocked.",
      delete: "{{name}} is deleted.",
    },
    confirm: {
      reset: {
        title: "Reset access for {{name}}?",
        text: "Password, authenticator and recovery codes are removed and all sessions end. {{name}} gets a new setup link.",
        button: "Reset and send link",
      },
      disable: {
        title: "Disable {{name}}?",
        text: "All sessions end, including open support sessions. You can enable the account again later.",
        button: "Disable",
      },
      delete: {
        title: "Delete {{name}}?",
        text: "The account is removed for good. The audit log keeps their entries.",
        button: "Delete",
      },
    },
  },
  audit: {
    title: "Audit log",
    description: "Everything operators did: sign-ins, support access and changes.",
    when: "When",
    who: "Operator",
    what: "Action",
    target: "Target",
    ip: "IP address",
    actions: {
      auth: {
        login: "Signed in",
        logout: "Signed out",
        password_failed: "Wrong password",
        code_failed: "Wrong code",
        break_glass: "Used the emergency sign-in",
      },
      account: {
        setup_completed: "Completed account setup",
        password_changed: "Changed password",
        recovery_codes_regenerated: "Created new recovery codes",
      },
      admin: {
        invited: "Invited operator",
        reset: "Reset operator access",
        disabled: "Disabled operator",
        enabled: "Enabled operator",
        unlocked: "Unlocked operator",
        deleted: "Deleted operator",
      },
      org: {
        updated: "Edited organization",
        sync_requested: "Started a sync",
        sync_cancelled: "Stopped a sync",
        connection_tested: "Tested the connection",
        suspended: "Suspended organization",
        resumed: "Resumed organization",
        deleted: "Deleted organization",
        support_access_started: "Started support access",
        support_access_ended: "Ended support access",
      },
      partner: {
        suspended: "Suspended IT partner",
        resumed: "Resumed IT partner",
        invoice_approved: "Approved payment by invoice",
        invoice_rejected: "Declined payment by invoice",
      },
      user: { signed_out: "Signed out a person everywhere" },
      email: { test_sent: "Sent a test email" },
      settings: {
        maintenance_on: "Turned maintenance mode on",
        maintenance_off: "Turned maintenance mode off",
      },
    },
  },
  account: {
    title: "My account",
    description: "Password, authenticator and recovery codes.",
    profile: "Profile",
    twoFactor: "Two-factor authentication",
    twoFactorOn: "On (authenticator app)",
    twoFactorZuvo: "On (zuvo sign-in with passkey)",
    zuvoManaged: "You sign in with zuvo. Your passkey and access are managed in the Zuvo Hub.",
    password: "Password",
    passwordHint: "Changing it signs out your other sessions.",
    currentPassword: "Current password",
    changePassword: "Change password",
    passwordChanged: "Password changed. Your other sessions were signed out.",
    recovery: "Recovery codes",
    recoveryHint_one: "{{count}} unused code left.",
    recoveryHint_other: "{{count}} unused codes left.",
    regenerate: "Create new codes",
    newCodes: "New recovery codes",
    lostPhone:
      "New phone or lost the authenticator app? Ask another operator to reset your access; you then set up password and app again.",
  },
  system: {
    title: "System",
    description: "State of the platform, integrations and background jobs.",
    status: "Status",
    environment: "Environment",
    version: "Version",
    noVersion: "None (not from a deployment)",
    database: "Database",
    dbOk: "Reachable ({{ms}} ms)",
    dbDown: "Not reachable",
    maintenance: "Service",
    microsoft: "Microsoft sign-in",
    email: "Email sending",
    emailWebhook: "Email delivery status",
    billing: "Billing (Stripe)",
    backups: "Backups",
    backupsHint: "Run and tested by the zuvo team outside the app.",
    on: "On",
    off: "Off",
    jobs: "Background jobs",
    queue: "{{active}} active, {{queued}} queued",
  },
  events: {
    title: "Events",
    description:
      "The standard zuvo events Kontaktfix recorded. They go to the Zuvo Hub once it is connected.",
    filter: "Filter by event",
    all: "All events",
    empty: "No events yet.",
    when: "When",
    name: "Event",
    properties: "Details",
    hub: "Zuvo Hub",
    delivered: "Sent",
    waiting: "Waiting",
  },
  leads: {
    title: "Leads",
    description: "Contact and waitlist forms of the landing page. Every operator gets an email for each one.",
    empty: "No leads yet.",
    when: "Received",
    who: "From",
    message: "Message",
    kind: "Form",
    kinds: { contact: "Contact", waitlist: "Waitlist" },
  },
  bugReports: {
    title: "Problem reports",
    description:
      'Reports from "Report a problem" in the app and here. Each one becomes a GitHub issue in the product repository.',
    empty: "No reports yet.",
    detail: "Problem report",
    when: "Reported",
    org: "Organization",
    fromOps: "Operator dashboard",
    page: "Page",
    browser: "Browser",
    release: "Version",
    errors: "Recent errors",
    issue: "GitHub issue",
    issueCreated: "Created",
    issueFailed: "Failed",
    noIssue: "Not yet (GITHUB_ISSUES_TOKEN not set or pending)",
  },
  settings: {
    title: "Settings",
    description: "Settings for the whole platform.",
    maintenance: "Maintenance mode",
    maintenanceHint:
      "Customers see a maintenance page and the API answers 503. Operators and support sessions keep working, delivery in the background continues.",
    maintenanceOn: "Maintenance mode is on.",
    maintenanceOff: "Maintenance mode is off.",
    activeSince: "On since {{date}}, by {{name}}.",
    inactive: "Off. Kontaktfix is available to every customer.",
  },
};
